import axios from 'axios';
import Cookies from 'js-cookie';
import { BACKEND_API_URL } from '../utils/api';

const getAuthHeaders = () => ({
    Authorization: `Token ${Cookies.get('access_token')}`,
    'Content-Type': 'application/json',
});

/**
 * View constants for user preferences
 */
export const VIEW_TYPES = {
    CHANNEL_VIEW: 1,
    FOCUS_MODE_VIEW: 2,
};

/**
 * Get user preference for a specific view
 * @param {number} viewId - View identifier (e.g., VIEW_TYPES.CHANNEL_VIEW)
 * @returns {Promise<Object>} Preference data or empty object if not found
 */
export const getUserPreference = async (viewId) => {
    try {
        const response = await axios.get(
            `${BACKEND_API_URL}/api/user/preferences/by-view/${viewId}/`,
            { headers: getAuthHeaders() }
        );
        return response.data.data || {};
    } catch (error) {
        if (error.response?.status === 404) {
            return {};
        }
        console.error('Error fetching user preference:', error);
        return {};
    }
};

/**
 * Save or update user preference for a specific view
 * @param {number} viewId - View identifier (e.g., VIEW_TYPES.CHANNEL_VIEW)
 * @param {Object} data - Preference data to save
 * @returns {Promise<Object>} Updated preference data
 */
export const saveUserPreference = async (viewId, data) => {
    try {
        const response = await axios.patch(
            `${BACKEND_API_URL}/api/user/preferences/by-view/${viewId}/`,
            { data },
            { headers: getAuthHeaders() }
        );
        return response.data.data || {};
    } catch (error) {
        // If preference doesn't exist, create it
        if (error.response?.status === 404) {
            try {
                const createResponse = await axios.post(
                    `${BACKEND_API_URL}/api/user/preferences/`,
                    { view: viewId, data },
                    { headers: getAuthHeaders() }
                );
                return createResponse.data.data || {};
            } catch (createError) {
                console.error('Error creating user preference:', createError);
                throw createError;
            }
        }
        console.error('Error saving user preference:', error);
        throw error;
    }
};

/**
 * Update user preference data (merges with existing data)
 * @param {number} viewId - View identifier
 * @param {Object} partialData - Partial data to merge with existing preference
 * @returns {Promise<Object>} Updated preference data
 */
export const updateUserPreference = async (viewId, partialData) => {
    try {
        // First, get existing preference
        const existing = await getUserPreference(viewId);
        // Merge with new data
        const mergedData = { ...existing, ...partialData };
        // Save merged data
        return await saveUserPreference(viewId, mergedData);
    } catch (error) {
        console.error('Error updating user preference:', error);
        throw error;
    }
};

